import { Box, Skeleton, Stack } from "@mui/material";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import type { PartyDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { PartyType } from "Infrastructure/Api/Api";
import { AddressDisplay } from "Components/UserAccount/Shared/AddressDisplay";
import { DocumentsDisplay } from "Components/UserAccount/Shared/DocumentsDisplay";
import { CompanyDataDisplay } from "Components/UserAccount/Shared/Party/CompanyDataDisplay";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { usePartyActingPersonParty } from "Hooks/useUserActingPersonParty";

const RESOURCES = Resources.Components.Account;

type Props = {
  party: PartyDto;
};

export const MerchantPartyDetail = ({ party }: Props) => {
  const { t } = useResource();
  const { data: verification } = useVerificationsQuery();
  const { actingPartyData, isLoading: isActingPersonLoading } =
    usePartyActingPersonParty(party.publicID ?? undefined);

  const { type, addresses } = party;
  const isSelfEmployed = type === PartyType.SelfEmployed;
  const isLegalEntity = type === PartyType.LegalEntity;
  const isDocumentExpired =
    verification?.identityVerificationStatus === "Expired";

  // For LegalEntity merchants the ID documents live on the acting person (StatutoryBody /
  // ProxyHolder / …) — the LE itself has no PartyIdentification. Resolve docs through the
  // related party so both the first and second document sections render the same way OSVČ
  // sees them on their own profile.
  const docsParty = isLegalEntity ? actingPartyData : party;
  const docsPartyIdentification = docsParty?.partyIdentification;
  const docsPartyPublicID = docsParty?.publicID;

  const shouldShowDocuments =
    !!docsPartyIdentification &&
    (isSelfEmployed ||
      isLegalEntity ||
      !!docsPartyIdentification.identificationValidTo);

  const isResolvingDocs = isLegalEntity && isActingPersonLoading;

  return (
    <Stack>
      <Box mb={[2, 2, 4]}>
        <InfoBox
          variant="info"
          content={t(RESOURCES.PersonalDataChangeNotification)}
        />
      </Box>
      <CompanyDataDisplay party={party} />
      <AddressDisplay addresses={addresses} />
      {isResolvingDocs && (
        <Skeleton height={300} variant="rounded" sx={{ mt: "0.5rem" }} />
      )}
      {!isResolvingDocs && shouldShowDocuments && (
        <DocumentsDisplay
          partyIdentification={docsPartyIdentification}
          partyPublicID={docsPartyPublicID ?? undefined}
          isDocumentExpired={isDocumentExpired}
          isSecondDocumentRequired
        />
      )}
    </Stack>
  );
};
